import React from 'react';
import InfoModal from '@/components/molecules/InfoModal';
import ECCreationForm from '@/components/organisms/ECCreationForm';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { expenseRevenueService } from '@/services/expense-category.service';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { budgetService } from '@/services/budget.service';
import { ExpenseCategoryFormValues } from '@/services/types/expense-category.types';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

const ECEditPage = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxExpenseValue, setMaxExpenseValue] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [expenseCategory, setExpenseCategory] = useState<any>(null);
  const navigate = useNavigate();
  const { budgetId, ecId } = useParams();
  const location = useLocation();

  const ecData = location.state?.ecData as any | undefined;

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/expense-categories/${ecId}`,
      // This page is always pushed from the EC detail page above, so cancelling
      // should pop back to it rather than replace (which would create a
      // duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  const formDefaultValues = useMemo(
    () => ({
      name: expenseCategory?.name,
      description: expenseCategory?.description,
      ecmev: expenseCategory?.ecmev,
      ecmev_alert_threshold: expenseCategory?.ecmev_alert_threshold
    }),
    [expenseCategory]
  );

  useEffect(() => {
    getExpenseCategory();
  }, []);

  const getExpenseCategory = async () => {
    try {
      const { data: ecData } = await expenseRevenueService.getExpenseCategory(budgetId || '', ecId || '');
      setExpenseCategory(ecData);
      setMaxExpenseValue(ecData.remaining_ecmev);
    } catch (error) {
      console.error('Error getting expense category:', error);
    }
  };

  const handleSubmit = async (data: ExpenseCategoryFormValues) => {
    console.log('Submit', data);
    try {
      if (!budgetId) {
        throw new Error('BudgetId is required');
      }
      const response = await expenseRevenueService.updateExpenseCategory(budgetId, ecId || '', {
        name: data.name,
        description: data.description,
        ecmev: Number(data.ecmev),
        ecmev_alert_threshold: Number(data.ecmev_alert_threshold)
      });
      console.log('Response', response);
      toast.success('Expense category updated successfully');
      allowNavigation();
      // This page was pushed from the EC detail page, so pop back to it
      // instead of replacing — replacing to the same URL as the previous
      // history entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (error) {
      console.error('Error updating expense category:', error);
      toast.error('Failed to update expense category');
    }
  };

  return (
    <>
      <MobileAppBar title="Expense Category Edit" />

      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center w-[80%] mx-auto md:w-full uppercase md:capitalize font-semibold mb-5 md:text-left md:text-[24px] md:mb-6">
          Edit your Expense Category
        </h2>

        {/* FORM */}
        <ECCreationForm
          onSubmit={handleSubmit}
          defaultValues={formDefaultValues}
          isEditMode={true}
          maxExpenseValue={maxExpenseValue}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
          onCancel={handleCancel}
        />
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your expense category"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Expense Category Updated"
          message={<p>You have successfully updated the Expense Category!</p>}
          buttonText="Go to Expense Categories"
          onButtonClick={() => {
            setShowSuccessModal(false);
            navigate(-1);
          }}
        />
      )}
    </>
  );
};

export default ECEditPage;
